import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { Megaphone, ChevronRight, ShieldAlert } from "lucide-react";
import { guardianCopyFor } from "./guardianCopy";
import { AnnouncementAttachmentList } from "@/components/AnnouncementAttachments";

export default function GuardianCirculars() {
  const [open, setOpen] = useState(null);
  const [openError, setOpenError] = useState(null);
  const { t, language } = useI18n();
  const copy = guardianCopyFor(language);
  const emergencyLabel = language === "ar" ? "إعلان طارئ" : "Emergency";
  const visibleUntilLabel = language === "ar" ? "مرئي حتى" : "Visible until";
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["circulars-parent"],
    queryFn: async () => unwrap(await api.get("/circulars")),
  });

  const openCircular = async (c) => {
    setOpenError(null);
    try {
      const full = unwrap(await api.get(`/circulars/${c.id}`));
      setOpen(full);
      refetch();
    } catch (error) {
      setOpenError(errMsg(error));
    }
  };

  if (isLoading) return <Skeleton className="h-96 w-full" />;
  if (isError) {
    return (
      <Card className="border-[var(--error)]/30" data-testid="guardian-circulars-error">
        <CardContent className="py-10 text-center text-sm text-[var(--error)]">{errMsg(error)}</CardContent>
      </Card>
    );
  }
  const circulars = data || [];

  return (
    <div className="space-y-6" data-testid="guardian-circulars">
      <div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">{t("guardian.circularsTitle")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("guardian.circularsSubtitle")}</p>
      </div>

      <div className="space-y-3 stagger">
        {circulars.map((c) => {
          const isEmergency = c.kind === "emergency";
          return (
            <button
              key={c.id}
              onClick={() => openCircular(c)}
              data-testid={`circular-${c.id}`}
              className="w-full text-left"
            >
              <Card
                className={`border-[var(--subtle)] border-t-4 lift-card ${isEmergency ? "bg-[var(--error-bg)]" : "bg-[var(--warning-bg)]"}`}
                style={{ borderTopColor: isEmergency ? "var(--error)" : "var(--warning)" }}
              >
                <CardContent className="p-5 flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${isEmergency ? "bg-[var(--error-bg)] text-[var(--error)]" : "bg-[var(--warning-bg)] text-[var(--warning)]"}`}>
                  {c.kind === "emergency" ? <ShieldAlert className="h-5 w-5" /> : <Megaphone className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="font-semibold text-[var(--text-primary)] truncate">{c.title}</div>
                    {c.kind === "emergency" && (
                      <Badge className="bg-[var(--error)] text-white border-0">{emergencyLabel}</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">{formatDate(c.publish_at)}</div>
                  {c.visible_until && (
                    <div className="text-xs text-[var(--text-muted)] mt-1">{visibleUntilLabel} {formatDate(c.visible_until)}</div>
                  )}
                  {c.description && (
                    <p className="mt-1 text-sm text-[var(--text-secondary)] break-words" data-testid={`circular-description-${c.id}`}>
                      {c.description}
                    </p>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-[var(--text-muted)]" />
                </CardContent>
              </Card>
            </button>
          );
        })}
        {circulars.length === 0 && (
          <Card className="border-[var(--subtle)]">
            <CardContent className="py-10 text-center text-[var(--text-secondary)]">
              <Megaphone className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("guardian.noCircularsYet")}
            </CardContent>
          </Card>
        )}
      </div>

      {openError && (
        <Card className="border-[var(--error)]/30" data-testid="guardian-circular-detail-error">
          <CardContent className="py-4 text-sm text-[var(--error)]">{openError}</CardContent>
        </Card>
      )}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg" closeLabel={copy.close}>
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{open?.title}</DialogTitle>
            <DialogDescription className="sr-only">{t("guardian.circularsSubtitle")}</DialogDescription>
          </DialogHeader>
          <div className="text-xs text-[var(--text-muted)]">{formatDate(open?.publish_at)}</div>
          {open?.visible_until && (
            <div className="text-xs text-[var(--text-muted)]">{visibleUntilLabel} {formatDate(open.visible_until)}</div>
          )}
          <div className="prose prose-sm max-w-none mt-2 text-[var(--text-primary)]" dangerouslySetInnerHTML={{ __html: open?.body_html || "" }} />
          <AnnouncementAttachmentList record={open} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
